/**
 * Visite guidée générale, jouée une fois à la première connexion d'un compte client.
 * Monté dans le layout : il cible la navigation et doit survivre au changement de page.
 */
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAccountStorage } from "../../hooks/useAccountStorage";
import { spaceForType } from "../../utils/roles";
import { SETUP_ROOT } from "../screens/setup/setupRoutes";
import { welcomeTourSteps } from "./steps/welcomeSteps";
import { pausePageTours, resumePageTours } from "./tourQueue";
import { hasSeenTour } from "./tourStorage";
import { useTour, WELCOME_PRIORITY } from "./useTour";

const TOUR_ID = "welcome";

export const WelcomeTour = () => {
  const { pathname } = useLocation();
  const { getAccountStorage } = useAccountStorage();
  const space = spaceForType(getAccountStorage()?.type);
  // Reporté pendant la mise en route : ce parcours a son propre mot d'accueil, et deux
  // bienvenues superposées à la première connexion n'en font pas une bonne. Le tour se
  // jouera à l'arrivée sur le dashboard, non vu donc non consommé.
  const shouldRun =
    space !== "admin" &&
    !hasSeenTour(TOUR_ID) &&
    !pathname.startsWith(SETUP_ROOT);

  // D'abord la vue d'ensemble, PUIS chaque page à son ouverture : sans cette pause, le
  // tutoriel du dashboard s'enchaînerait aussitôt. La levée compte autant — la file est
  // un singleton qui survit à la déconnexion.
  useEffect(() => {
    if (shouldRun) pausePageTours();
    else resumePageTours();
  }, [shouldRun]);

  const initialPath = useRef(pathname);
  useEffect(() => {
    if (pathname !== initialPath.current) resumePageTours();
  }, [pathname]);

  useTour({
    id: TOUR_ID,
    steps: welcomeTourSteps(space),
    autoStart: shouldRun,
    priority: WELCOME_PRIORITY,
    // Plus court que celui des tutoriels de page : l'accueil doit gagner la file.
    delayMs: 500,
    pausable: false,
  });

  return null;
};
